import React, { useCallback, useEffect, useState } from "react";
import PortalHeader from "@/components/PortalHeader";
import MonthSelect from "@/components/MonthSelect";
import ContentEditor from "@/components/admin/ContentEditor";
import PostsManager from "@/components/admin/PostsManager";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Save, UserPlus, Trash2, DollarSign, PhoneCall, Users, Headphones, Activity, FileDown, Loader2, Inbox, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { downloadStatement, monthOptions } from "@/lib/statements";

const STATUS_COLOR = {
  ringing: "bg-amber-100 text-amber-700 border-amber-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ended: "bg-slate-100 text-slate-600 border-slate-200",
  missed: "bg-rose-100 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const QUOTE_STATUS_COLOR = {
  new: "bg-amber-100 text-amber-700 border-amber-200",
  contacted: "bg-blue-100 text-blue-700 border-blue-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const INDUSTRIES = ["Healthcare", "Legal", "Business", "Other"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [rates, setRates] = useState({ customer_rate_per_min: 0, interpreter_rate_per_min: 0 });
  const [languages, setLanguages] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [calls, setCalls] = useState([]);
  const [custBilling, setCustBilling] = useState([]);
  const [intBilling, setIntBilling] = useState([]);
  const [newLang, setNewLang] = useState("");
  const [stmtMonth, setStmtMonth] = useState(monthOptions(12)[0].value);

  const loadAll = useCallback(async () => {
    try {
      const [s, r, l, i, cu, q, c, cb, ib] = await Promise.all([
        api.get("/admin/stats"), api.get("/rates"), api.get("/languages"),
        api.get("/interpreters"), api.get("/customers"), api.get("/quotes"),
        api.get("/calls"), api.get("/billing/customers"), api.get("/billing/interpreters"),
      ]);
      setStats(s.data); setRates(r.data); setLanguages(l.data);
      setInterpreters(i.data); setCustomers(cu.data); setQuotes(q.data);
      setCalls(c.data); setCustBilling(cb.data); setIntBilling(ib.data);
    } catch (e) {
      console.warn("[admin] load failed", e);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const saveRates = async () => {
    try {
      await api.patch("/rates", {
        customer_rate_per_min: Number(rates.customer_rate_per_min),
        interpreter_rate_per_min: Number(rates.interpreter_rate_per_min),
      });
      toast.success("Rates updated");
      loadAll();
    } catch { toast.error("Could not save rates"); }
  };

  const addLanguage = async () => {
    if (!newLang.trim()) return;
    try {
      await api.post("/languages", { name: newLang.trim() });
      setNewLang("");
      toast.success("Language added");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };

  const deleteLanguage = async (name) => {
    try { await api.delete(`/languages/${encodeURIComponent(name)}`); loadAll(); }
    catch { toast.error("Could not delete"); }
  };

  const deleteInterpreter = async (id) => {
    if (!window.confirm("Delete this interpreter?")) return;
    try { await api.delete(`/interpreters/${id}`); loadAll(); toast.success("Interpreter removed"); }
    catch { toast.error("Could not delete"); }
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); loadAll(); toast.success("Customer removed"); }
    catch { toast.error("Could not delete"); }
  };

  const toggleActive = async (u, kind) => {
    await api.patch(`/${kind}/${u.id}`, { active: !u.active });
    loadAll();
  };

  const markQuote = async (q, status) => {
    await api.patch(`/quotes/${q.id}`, { status });
    loadAll();
  };

  return (
    <div className="min-h-screen">
      <PortalHeader title="Admin" />
      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={<PhoneCall className="h-4 w-4" />} label="Total calls" value={stats?.total_calls ?? "—"} />
          <StatCard icon={<Activity className="h-4 w-4" />} label="Interpreters online" value={stats?.active_interpreters ?? "—"} />
          <StatCard icon={<DollarSign className="h-4 w-4" />} label="Revenue" value={stats ? `$${stats.total_revenue.toFixed(2)}` : "—"} />
          <StatCard icon={<DollarSign className="h-4 w-4" />} label="Payouts" value={stats ? `$${stats.total_payouts.toFixed(2)}` : "—"} />
          <StatCard icon={<Users className="h-4 w-4" />} label="Customers" value={stats?.total_customers ?? "—"} />
          <StatCard icon={<Headphones className="h-4 w-4" />} label="Interpreters" value={stats?.total_interpreters ?? "—"} />
          <StatCard icon={<Inbox className="h-4 w-4" />} label="New quotes" value={stats?.new_quotes ?? "—"} />
          <StatCard icon={<DollarSign className="h-4 w-4" />} label="Net (rev - pay)" value={stats ? `$${(stats.total_revenue - stats.total_payouts).toFixed(2)}` : "—"} />
        </section>

        <Tabs defaultValue="interpreters" className="w-full">
          <TabsList data-testid="admin-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="interpreters" data-testid="tab-interpreters">Interpreters</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="calls" data-testid="tab-calls">Call logs</TabsTrigger>
            <TabsTrigger value="billing-cust" data-testid="tab-billing-customers">Customer billing</TabsTrigger>
            <TabsTrigger value="billing-int" data-testid="tab-billing-interpreters">Interpreter payouts</TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-rates">Rates</TabsTrigger>
            <TabsTrigger value="languages" data-testid="tab-languages">Languages</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Website content</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts">Posts</TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export">Export</TabsTrigger>
          </TabsList>

          {/* Interpreters */}
          <TabsContent value="interpreters" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display text-xl">Interpreters</CardTitle>
                <AddInterpreterDialog languages={languages} onAdded={loadAll} />
              </CardHeader>
              <CardContent>
                <Table data-testid="interpreters-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
                      <TableHead>Languages</TableHead><TableHead>Payout $/min</TableHead><TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {interpreters.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No interpreters yet</TableCell></TableRow>
                    )}
                    {interpreters.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell className="text-slate-500">{i.ref_id}</TableCell>
                        <TableCell className="font-medium">{i.name}</TableCell>
                        <TableCell className="text-slate-500">{i.email}</TableCell>
                        <TableCell>{i.languages.join(", ")}</TableCell>
                        <TableCell>{i.payout_rate_per_min != null ? `$${i.payout_rate_per_min.toFixed(2)}` : "default"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={i.active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                              {i.active ? "Active" : "Pending"}
                            </Badge>
                            <Badge variant="outline" className={i.online ? "bg-teal-100 text-teal-700 border-teal-200" : "bg-slate-100 text-slate-500 border-slate-200"}>
                              {i.online ? "Online" : "Offline"}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => toggleActive(i, "interpreters")} data-testid={`toggle-interp-${i.id}`}>
                            {i.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteInterpreter(i.id)} data-testid={`delete-interpreter-${i.id}`}
                            className="text-rose-600 hover:text-rose-700 ml-2">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customers */}
          <TabsContent value="customers" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display text-xl">Customers (service providers)</CardTitle>
                <AddCustomerDialog onAdded={loadAll} />
              </CardHeader>
              <CardContent>
                <Table data-testid="customers-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead><TableHead>Company</TableHead><TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Industry</TableHead>
                      <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No customers yet</TableCell></TableRow>
                    )}
                    {customers.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="text-slate-500">{c.ref_id}</TableCell>
                        <TableCell className="font-medium">{c.company_name || "—"}</TableCell>
                        <TableCell>{c.contact_name || "—"}</TableCell>
                        <TableCell className="text-slate-500">{c.email}</TableCell>
                        <TableCell className="text-slate-500">{c.phone || "—"}</TableCell>
                        <TableCell>{c.industry || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={c.active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                            {c.active ? "Active" : "Pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => toggleActive(c, "customers")} data-testid={`toggle-customer-${c.id}`}>
                            {c.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteCustomer(c.id)} data-testid={`delete-customer-${c.id}`}
                            className="text-rose-600 hover:text-rose-700 ml-2">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes */}
          <TabsContent value="quotes" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-display text-xl">Quote requests</CardTitle></CardHeader>
              <CardContent>
                <Table data-testid="quotes-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Company</TableHead><TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead><TableHead>Message</TableHead><TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No quote requests yet</TableCell></TableRow>
                    )}
                    {quotes.map((q) => (
                      <TableRow key={q.id}>
                        <TableCell className="text-slate-500">{new Date(q.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="font-medium">{q.company_name}</TableCell>
                        <TableCell className="text-slate-500">{q.email}</TableCell>
                        <TableCell className="text-slate-500">{q.phone}</TableCell>
                        <TableCell className="text-slate-500 max-w-[220px] truncate">{q.message || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className={QUOTE_STATUS_COLOR[q.status]}>{q.status}</Badge></TableCell>
                        <TableCell className="text-right space-x-2">
                          {q.status === "new" && (
                            <Button variant="outline" size="sm" onClick={() => markQuote(q, "contacted")} data-testid={`quote-contacted-${q.id}`}>
                              Mark contacted
                            </Button>
                          )}
                          {q.status !== "closed" && (
                            <Button variant="ghost" size="sm" onClick={() => markQuote(q, "closed")} data-testid={`quote-close-${q.id}`}>
                              Close
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Call logs */}
          <TabsContent value="calls" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-display text-xl">Call logs</CardTitle></CardHeader>
              <CardContent>
                <Table data-testid="calls-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Interpreter</TableHead>
                      <TableHead>Language</TableHead><TableHead>Duration</TableHead><TableHead>Charge</TableHead>
                      <TableHead>Payout</TableHead><TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calls.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No calls yet</TableCell></TableRow>
                    )}
                    {calls.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="text-slate-500">{new Date(c.started_at).toLocaleString()}</TableCell>
                        <TableCell>{c.customer_company || c.customer_name}</TableCell>
                        <TableCell>{c.interpreter_name || "—"}</TableCell>
                        <TableCell>{c.language}</TableCell>
                        <TableCell>{fmt(c.duration_seconds || 0)}</TableCell>
                        <TableCell>${(c.customer_charge || 0).toFixed(2)}</TableCell>
                        <TableCell>${(c.interpreter_payout || 0).toFixed(2)}</TableCell>
                        <TableCell><Badge variant="outline" className={STATUS_COLOR[c.status]}>{c.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customer billing */}
          <TabsContent value="billing-cust" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display text-xl">Customer billing</CardTitle>
                <MonthSelect value={stmtMonth} onChange={setStmtMonth} testid="customer-statement-month" />
              </CardHeader>
              <CardContent>
                <Table data-testid="customer-billing-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead><TableHead>Email</TableHead><TableHead>Total calls</TableHead>
                      <TableHead>Total minutes</TableHead><TableHead>Total charge</TableHead>
                      <TableHead className="text-right">Statement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {custBilling.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No billed calls yet</TableCell></TableRow>
                    )}
                    {custBilling.map((r) => (
                      <TableRow key={r.customer_id}>
                        <TableCell>{r.customer_company || r.customer_name}{r.customer_ref_id ? <span className="text-slate-400 text-xs ml-1">({r.customer_ref_id})</span> : null}</TableCell>
                        <TableCell className="text-slate-500">{r.customer_email || "—"}</TableCell>
                        <TableCell>{r.total_calls}</TableCell>
                        <TableCell>{r.total_minutes.toFixed(2)}</TableCell>
                        <TableCell>${r.total_charge.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <StatementButton type="customer" userId={r.customer_id} ym={stmtMonth} testid={`dl-cust-statement-${r.customer_id}`} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interpreter payouts */}
          <TabsContent value="billing-int" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display text-xl">Interpreter payouts</CardTitle>
                <MonthSelect value={stmtMonth} onChange={setStmtMonth} testid="interpreter-statement-month" />
              </CardHeader>
              <CardContent>
                <Table data-testid="interpreter-billing-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Interpreter</TableHead><TableHead>Total calls</TableHead>
                      <TableHead>Total minutes</TableHead><TableHead>Total payout</TableHead>
                      <TableHead className="text-right">Statement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {intBilling.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No billed calls yet</TableCell></TableRow>
                    )}
                    {intBilling.map((r) => (
                      <TableRow key={r.interpreter_id}>
                        <TableCell>{r.interpreter_name}</TableCell>
                        <TableCell>{r.total_calls}</TableCell>
                        <TableCell>{r.total_minutes.toFixed(2)}</TableCell>
                        <TableCell>${r.total_payout.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <StatementButton type="interpreter" userId={r.interpreter_id} ym={stmtMonth} testid={`dl-int-statement-${r.interpreter_id}`} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rates */}
          <TabsContent value="rates" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-display text-xl">Per-minute rates</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Customer rate ($/min)</Label>
                    <Input type="number" step="0.01" value={rates.customer_rate_per_min}
                      data-testid="customer-rate-input"
                      onChange={(e) => setRates({ ...rates, customer_rate_per_min: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Default interpreter payout ($/min)</Label>
                    <Input type="number" step="0.01" value={rates.interpreter_rate_per_min}
                      data-testid="interpreter-rate-input"
                      onChange={(e) => setRates({ ...rates, interpreter_rate_per_min: e.target.value })} />
                  </div>
                </div>
                <Button onClick={saveRates} data-testid="save-rates-btn"
                  className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
                  <Save className="h-4 w-4 mr-1" /> Save rates
                </Button>
                <p className="text-sm text-slate-500">
                  These apply to new calls. Individual interpreter payout overrides can be set per interpreter.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Languages */}
          <TabsContent value="languages" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-display text-xl">Languages</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input value={newLang} onChange={(e) => setNewLang(e.target.value)} placeholder="Add language e.g. Vietnamese"
                    data-testid="new-language-input" />
                  <Button onClick={addLanguage} data-testid="add-language-btn"
                    className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {languages.map((l) => (
                    <div key={l.name} className="flex items-center gap-2 border border-slate-200 rounded-full px-3 py-1 bg-white">
                      <span className="text-sm">{l.name}</span>
                      <button className="text-slate-400 hover:text-rose-600" onClick={() => deleteLanguage(l.name)}
                        data-testid={`delete-lang-${l.name}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Website content CMS */}
          <TabsContent value="content" className="mt-6">
            <ContentEditor />
          </TabsContent>

          {/* Posts CMS */}
          <TabsContent value="posts" className="mt-6">
            <PostsManager />
          </TabsContent>

          {/* Full export */}
          <TabsContent value="export" className="mt-6">
            <ExportPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
        {icon}<span>{label}</span>
      </div>
      <div className="mt-2 font-display text-2xl font-medium text-[#0a2540]">{value}</div>
    </div>
  );
}

function StatementButton({ type, userId, ym, testid }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const [y, m] = ym.split("-");
      await downloadStatement(Number(y), Number(m), { type, user_id: userId });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download statement");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={go} disabled={busy} data-testid={testid}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
    </Button>
  );
}

function ExportPanel() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      a.href = url;
      a.download = match ? match[1] : "interpreta-export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate export");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <PackageOpen className="h-5 w-5" /> Full export
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-slate-600 max-w-2xl leading-relaxed">
          Download a single <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">.zip</code> bundle
          containing everything needed to run Interpreta on your own domain — no dependency on this workspace.
        </p>
        <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside max-w-2xl">
          <li>The complete codebase (backend, frontend source, documentation).</li>
          <li>A full database dump — all accounts, call history, billing, website content, blog posts, quotes, languages and rates, exactly as they exist right now.</li>
          <li><span className="font-medium">RESTORE.md</span> — step-by-step restore instructions, and <span className="font-medium">docs/OPERATIONS.md</span> — the full operations manual (hosting, backups, updates, GitHub).</li>
        </ul>
        <p className="text-xs text-slate-500 max-w-2xl">
          Sensitive runtime values (JWT secret, email key, admin password) are never included —
          the bundle ships config templates with placeholders instead.
        </p>
        <Button onClick={download} disabled={busy} data-testid="download-export-btn"
          className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90 h-12 px-8 font-semibold">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackageOpen className="h-4 w-4 mr-2" />}
          {busy ? "Building bundle…" : "Download full export"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Add Interpreter dialog
// ---------------------------------------------------------------------------
function AddInterpreterDialog({ languages, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", languages: [], payout_rate_per_min: "" });
  const [saving, setSaving] = useState(false);

  const toggle = (name) => {
    setForm((f) => ({
      ...f,
      languages: f.languages.includes(name) ? f.languages.filter((n) => n !== name) : [...f.languages, name],
    }));
  };

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, languages: form.languages,
        payout_rate_per_min: form.payout_rate_per_min ? Number(form.payout_rate_per_min) : null,
      };
      if (form.password) payload.password = form.password;
      const { data } = await api.post("/interpreters", payload);
      if (data?.email_sent) toast.success(`${form.name} added — invite email sent`);
      else if (data?.temp_password) toast.success(`${form.name} added. Email failed — share temp password: ${data.temp_password}`);
      else toast.success(`${form.name} added`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", languages: [], payout_rate_per_min: "" });
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add interpreter");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-interpreter-btn" className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
          <UserPlus className="h-4 w-4 mr-1" /> Add interpreter
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Add new interpreter</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input data-testid="new-interp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Payout $/min (optional)</Label>
              <Input data-testid="new-interp-rate" type="number" step="0.01" value={form.payout_rate_per_min}
                onChange={(e) => setForm({ ...form, payout_rate_per_min: e.target.value })} placeholder="uses default" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input data-testid="new-interp-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Temporary password (optional)</Label>
            <Input data-testid="new-interp-password" type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to auto-generate" />
            <p className="text-xs text-slate-500">They get an email invite with a set-password link. Account stays inactive until they set their password.</p>
          </div>
          <div className="space-y-2">
            <Label>Languages</Label>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-3">
              {languages.map((l) => (
                <label key={l.name} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.languages.includes(l.name)} onCheckedChange={() => toggle(l.name)}
                    data-testid={`new-interp-lang-${l.name}`} />
                  {l.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-interpreter-btn"
            className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
            {saving ? "Saving…" : "Create interpreter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add Customer dialog
// ---------------------------------------------------------------------------
function AddCustomerDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", industry: "Other", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Company, contact name and email required"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.phone) delete payload.phone;
      const { data } = await api.post("/customers", payload);
      if (data?.email_sent) toast.success(`${form.company_name} added — invite email sent`);
      else if (data?.temp_password) toast.success(`${form.company_name} added. Email failed — temp password: ${data.temp_password}`);
      else toast.success(`${form.company_name} added`);
      setOpen(false);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", industry: "Other", password: "" });
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add customer");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-customer-btn" className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
          <UserPlus className="h-4 w-4 mr-1" /> Add customer
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Add new customer (service provider)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Company name</Label>
            <Input data-testid="new-customer-company" value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Health Systems" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contact person</Label>
              <Input data-testid="new-customer-contact" value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Jane Cooper" />
            </div>
            <div className="space-y-2">
              <Label>Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                <SelectTrigger data-testid="new-customer-industry"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => (
                    <SelectItem key={i} value={i} data-testid={`industry-option-${i}`}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="new-customer-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input data-testid="new-customer-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 000-0000" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Temporary password (optional)</Label>
            <Input data-testid="new-customer-password" type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to auto-generate" />
            <p className="text-xs text-slate-500">They get an email invite with a set-password link.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-customer-btn"
            className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
            {saving ? "Saving…" : "Create customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

